import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Dialog, Popover, Tag, Tooltip } from '@scholastic/mockingjay';
import { getBgGradientById } from 'eds-common';
import InfoIcon from '@data/assets/info-icon.svg';
import ChevronLeftIcon from '@data/assets/chevronLeft.svg';
import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';
import { Metrics } from '@data/components/composite/Metrics/Metrics';
import {
  ADMIN_PRIMARY_METRICS,
  ADMIN_SECONDARY_METRICS,
  OVERVIEW_PRIMARY_METRICS,
  OVERVIEW_SECONDARY_METRICS,
  OVERVIEW_TABLE_COLUMNS,
} from '@data/config/overview-metric';
import { NON_MAGAZINE_PRODUCTS, type ProductCode } from '@data/config/reports';
import type { MetricV2Response } from '@data/config/metrics';
import { useApplicationsHierarchy } from '@data/hooks/api/use-applications-hierarchy-api/use-applications-hierarchy-api';
import { useAdminMetricsApi } from '@data/hooks/api/use-admin-metrics-api/use-admin-metrics-api';
import { useMetricsBatchV2Api } from '@data/hooks/api/use-metrics-batch-v2-api/use-metrics-batch-v2-api';
import { getEdApps } from '@data/utils/get-ed-apps';
import { getGradeDisplay } from '@data/utils/get-grade-range';
import { getAccessTags } from '@data/utils/get-access-tags';
import { cn } from '@data/utils/cn/cn';
import {
  currentDrilldown,
  useAdminReportOutletContext,
  type AdminReportOutletContext,
} from '@data/pages/AdminReport/AdminReportLayout/AdminReportLayout';
import { Drilldown } from '@data/components/report/ReportFilterBar/ReportFilterBar';
import { AdminNavDrawer } from '@data/components/report/AdminReportDrawers/AdminReportDrawers';
import { Table } from '@data/components/basic/Table/Table';
import {
  AdminMetricTableType,
  useAdminMetricsTableApi,
} from '@data/hooks/api/use-admin-metrics-table-api/use-admin-metrics-table-api';
import type { MetricsTableV2Response } from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';
import {
  MetricTableType,
  useMetricsTableV2Api,
} from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';
import { useTableControls } from '@data/hooks/use-report-tables/use-table-controls/use-table-controls';
import { buildOrderBy } from '@data/utils/build-order-by';
import { filterTableData } from '@data/utils/filter-table-data';
import { useExportToCSVHandler } from '@data/hooks/use-report-tables/use-export-to-csv-handler/use-export-to-csv-handler';
import { useOrgLabel } from '@data/hooks/use-org-label/use-org-label';
import {
  useEffectiveClassIds,
  useEffectiveStudentIds,
} from '@data/hooks/use-effective-filters/use-effective-filters';
import { resolveMetrics } from '@data/utils/resolve-metrics';
import formatDate from '@data/utils/format-date';

const ORGANIZATION_COLUMN = 'ORGANIZATION';
const TEACHER_COLUMN = 'TEACHER';
const CLASS_COLUMN = 'CLASS';
const STUDENT_COLUMN = 'STUDENT';

type Application = NonNullable<
  ReturnType<typeof useApplicationsHierarchy>['data']
>[number];

/**
 * The product's grade, tags and description.
 *
 * Printed under the title where there is room; below the tablet breakpoint
 * there is not, so the same detail opens in a dialog from the title instead.
 */
function ProductDetailsDialog({ application }: { application: Application }) {
  const gradeDisplay = getGradeDisplay(application);
  const accessTags = getAccessTags(application);

  return (
    <div className="tablet:hidden">
      {/* The card carries the design's own size and spacing; `[&>h2]:mb-1`
          tightens the title to the 4px it leaves above the grade row. */}
      <Dialog
        className="w-[calc(100vw-2rem)] min-w-[273px] max-w-[400px] rounded-sm p-4 pt-10 shadow-2xl [&>h2]:mb-1"
        header={
          <img
            src={application.applicationIconSrc}
            alt={`${application.applicationLabel} logo`}
            className="mb-3 block h-8 object-contain"
          />
        }
        title={application.applicationLabel}
        trigger={
          <button
            type="button"
            aria-label={`About ${application.applicationLabel}`}
            className="flex size-4 shrink-0 items-center justify-center"
          >
            <InfoIcon aria-hidden="true" />
          </button>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          {gradeDisplay && (
            <p className="flex items-center gap-1 text-sm font-bold leading-normal text-[#333]">
              Grade{' '}
              <span className="text-base font-normal">{gradeDisplay}</span>
            </p>
          )}
          {accessTags.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </div>

        {application.applicationDescription && (
          <p className="mt-3 text-base leading-normal text-neutral-900">
            {application.applicationDescription}
          </p>
        )}
      </Dialog>
    </div>
  );
}

/** The product header, at the top of the report panel. */
function ProductHeader({
  productId,
  drilldown,
}: {
  productId: string;
  drilldown: Drilldown;
}) {
  const { data: applications, isLoading } = useApplicationsHierarchy();
  const application = applications?.find(
    (app) => app.applicationCode === productId
  );

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading" className="flex flex-col gap-2">
        <LoadingBar className="h-9 w-full tablet:w-64" />
        <LoadingBar className="w-20" />
        <LoadingBar className="w-full max-w-[650px]" />
      </div>
    );
  }

  if (!application) return null;

  const gradeDisplay = getGradeDisplay(application);

  const accessTags = getAccessTags(application);

  return (
    <div className="flex items-start justify-between gap-6">
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          {/* The nav has no room of its own below laptop, so it opens here. */}
          <AdminNavDrawer drilldown={drilldown} />

          <h1 className="min-w-0 flex-1 text-3xl font-bold leading-9 antialiased">
            {application.applicationLabel} | Overview Report
          </h1>

          {/* Everything under the title is a dialog while there is no room
              to print it. */}
          <ProductDetailsDialog application={application} />
        </div>

        <div className="mt-1 hidden flex-wrap items-center gap-2 tablet:flex">
          {gradeDisplay && (
            <p className="flex items-center gap-1 text-sm font-bold text-[#333]">
              Grade{' '}
              <span className="text-base font-normal">{gradeDisplay}</span>
            </p>
          )}

          {accessTags.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </div>

        {application.applicationDescription && (
          <p className="mt-2 hidden max-w-[650px] text-base leading-normal text-neutral-900 tablet:flex">
            {application.applicationDescription}
          </p>
        )}
      </div>

      <div className="hidden shrink-0 items-center self-center tablet:flex">
        <img
          src={application.applicationIconSrc}
          alt={`${application.applicationLabel} logo`}
          className="h-10 object-contain"
        />
      </div>
    </div>
  );
}

/**
 * The design's dashes are 17px long; CSS `border-dashed` draws ~3px ones and
 * offers no way to set the length, so the border is an SVG stroke instead.
 */
const DASHED_BORDER =
  "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23D4D4D4' stroke-width='2' stroke-dasharray='17' rx='8'/%3e%3c/svg%3e\")";

/** The design sets the text in a 640px column, centred in the panel. */
const NO_ACCESS_COLUMN = 'w-full max-w-[640px]';

/** Replaces the report when nothing in scope holds the product. */
function NoProductAccess({
  orgLabel,
  productLabel,
  isSingleOrg,
  onViewAllOrganizations,
}: {
  orgLabel: string;
  productLabel: string;
  /** One organization is named; any other number speaks of the selection. */
  isSingleOrg: boolean;
  /** Absent when nothing is selected, since there is nothing to clear. */
  onViewAllOrganizations?: () => void;
}) {
  const headline = isSingleOrg
    ? `${orgLabel} does not have access to ${productLabel}.`
    : `None of the selected organizations have access to ${productLabel}.`;
  const subject = isSingleOrg
    ? `${orgLabel} has`
    : 'the selected organizations have';

  return (
    <div
      className="flex w-full flex-col items-center justify-center gap-4 rounded-lg text-neutral-1300 mobile:px-6 mobile:py-12 tablet:p-20"
      style={{ backgroundImage: DASHED_BORDER }}
    >
      <p
        className={cn(
          NO_ACCESS_COLUMN,
          'text-2xl font-semibold leading-normal'
        )}
      >
        {headline}
      </p>
      <p
        className={cn(NO_ACCESS_COLUMN, 'text-base font-normal leading-normal')}
      >
        Please select an organization that does have access to {productLabel} or
        change the selected product report to a product that {subject} access
        to.
      </p>
      {onViewAllOrganizations && (
        <div className={NO_ACCESS_COLUMN}>
          <Button variant="primary" onClick={onViewAllOrganizations}>
            View all organizations
          </Button>
        </div>
      )}
    </div>
  );
}

/** Steps back out of a drill-down; absent at the level that has no parent. */
function BackControl({
  backTarget,
}: {
  backTarget: AdminReportOutletContext['backTarget'];
}) {
  const navigate = useNavigate();

  if (!backTarget) return null;

  return (
    <Tooltip content={backTarget.label} side="top" align="start">
      {/* The bare chevron, not the bordered icon the shared control uses:
          here the circle only appears on hover. 24px with 4px padding, as
          the design's small back button is drawn. */}
      <button
        aria-label={backTarget.label}
        className="mt-1 flex size-6 shrink-0 items-center justify-center rounded-full p-1 hover:bg-neutral-100"
        onClick={() => navigate(backTarget.to)}
        type="button"
      >
        <ChevronLeftIcon aria-hidden="true" className="size-4" />
      </button>
    </Tooltip>
  );
}

/** The highlights panel: back control, heading, optional count, metrics. */
function HighlightsCard({
  title,
  countLabel,
  count,
  backTarget,
  /** Colours the stripe along the card's top edge; omitted leaves it off. */
  gradientId,
  children,
}: {
  title: string;
  countLabel?: string;
  count?: ReactNode;
  backTarget?: AdminReportOutletContext['backTarget'];
  gradientId?: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-8 overflow-hidden rounded-lg bg-white shadow-[0_1px_1.5px_rgba(0,0,0,0.1),0_1px_1px_rgba(0,0,0,0.1)]">
      {gradientId && (
        <div
          className={cn('h-3 w-full', getBgGradientById(gradientId))}
          data-testid="highlights-colour-bar"
          aria-hidden
        />
      )}

      <div className="flex flex-col gap-4 px-4 py-6">
        <div className="flex items-start gap-2">
          <BackControl backTarget={backTarget} />
          <div className="min-w-0 flex-1">
            <h3 className="break-words text-xl font-normal text-neutral-1300">
              Highlights: <span className="font-bold">{title}</span>
            </h3>
            {countLabel && (
              <p className="mt-2 text-xs font-bold text-neutral-1300">
                {countLabel}
                <span className="ml-2 text-sm font-normal">{count ?? '—'}</span>
              </p>
            )}
          </div>
        </div>

        <section aria-label="Highlights">{children}</section>
      </div>
    </div>
  );
}

/**
 * Names the admin's own organization for its direct educators.
 *
 * A no-op on the drill-down table, which has no organization column.
 */
function labelOrgRows(
  table: MetricsTableV2Response,
  orgLabel: (orgId: string, orgName: string) => string
): MetricsTableV2Response {
  const idIndex = table.columns.findIndex((col) => col.type === 'id');
  const nameIndex = table.columns.findIndex(
    (col) => col.key === ORGANIZATION_COLUMN
  );
  if (idIndex < 0 || nameIndex < 0) return table;

  return {
    ...table,
    rows: table.rows.map((row) =>
      row.map((cell, index) =>
        index === nameIndex
          ? orgLabel(String(row[idIndex]), String(cell))
          : cell
      )
    ),
  };
}

/**
 * Which table each roster level shows: the educator level tabulates their
 * classes, the class level its students. The student level is one student —
 * there is nothing below it to tabulate.
 */
const ROSTER_TABLE_BY_LEVEL: Partial<Record<Drilldown, MetricTableType>> = {
  [Drilldown.Teacher]: MetricTableType.AllClassOverview,
  [Drilldown.Class]: MetricTableType.IndividualClassOverview,
};

/** No request until every filter the level scopes by names its own ids. */
function canReadRoster({
  enabled,
  orgId,
  teacherIds,
  classIds,
  studentsReady,
}: {
  enabled: boolean;
  orgId: string;
  teacherIds: string[];
  classIds: string[];
  studentsReady: boolean;
}) {
  return (
    enabled &&
    !!orgId &&
    teacherIds.length > 0 &&
    classIds.length > 0 &&
    studentsReady
  );
}

/**
 * What the hierarchy says this product is made of — not what the reader holds.
 * An admin need not be entitled to the product they are reporting on.
 */
function hierarchyProductCodes(
  applications: ReturnType<typeof useApplicationsHierarchy>['data'],
  productId: string
) {
  return applications
    ?.find((app) => app.applicationCode === productId)
    ?.products.map((product) => product.productCode);
}

/**
 * The roster figures for one level, read the way the teacher's own Overview
 * reads them. Magazines and rise have no secondary section, so the two
 * reports show the same tiles.
 */
function rosterMetricTypes(
  productId: ProductCode,
  level: Drilldown,
  productCodes: string[] | undefined
) {
  return {
    primaryMetricTypes: resolveMetrics(
      OVERVIEW_PRIMARY_METRICS,
      productId,
      level,
      productCodes
    ),
    secondaryMetricTypes:
      (NON_MAGAZINE_PRODUCTS as string[]).includes(productId) &&
      productId !== 'rise'
        ? resolveMetrics(
            OVERVIEW_SECONDARY_METRICS,
            productId,
            level,
            productCodes
          )
        : [],
  };
}

/** Each level is named for what it reports on. */
function headingTitleFor(
  {
    isStudentDrilldown,
    isClassDrilldown,
    isTeacherDrilldown,
  }: AdminReportOutletContext,
  labels: {
    selectedOrgLabel: string;
    selectedTeacherLabel: string;
    selectedClassLabel: string;
    selectedStudentLabel: string;
  }
) {
  if (isStudentDrilldown) return labels.selectedStudentLabel;
  if (isClassDrilldown) return labels.selectedClassLabel;
  if (isTeacherDrilldown) return labels.selectedTeacherLabel;
  return labels.selectedOrgLabel;
}

/**
 * The bar is coloured from the thing the level reports on, and the sub-line
 * counts the level below. The student level has no level below it.
 */
function headingCountFor(
  context: AdminReportOutletContext,
  totalCount: number,
  classCount: number | undefined
) {
  const {
    isStudentDrilldown,
    isClassDrilldown,
    isTeacherDrilldown,
    teacherIds,
    classIds,
    studentIds,
  } = context;

  if (isStudentDrilldown) {
    return studentIds[0] ? { gradientId: studentIds[0] } : {};
  }
  if (isClassDrilldown) {
    return {
      countLabel: 'Students',
      count: totalCount,
      ...(classIds[0] ? { gradientId: classIds[0] } : {}),
    };
  }
  if (isTeacherDrilldown) {
    return {
      countLabel: 'Classes',
      count: classCount,
      ...(teacherIds[0] ? { gradientId: teacherIds[0] } : {}),
    };
  }
  return {};
}

/** Each level's rows are a different thing, so each sorts by its own column. */
const SORT_COLUMN_BY_LEVEL: Partial<Record<Drilldown, string>> = {
  [Drilldown.AllOrganizations]: ORGANIZATION_COLUMN,
  [Drilldown.Organization]: TEACHER_COLUMN,
  [Drilldown.Teacher]: CLASS_COLUMN,
  [Drilldown.Class]: STUDENT_COLUMN,
};

/** What the page renders, whichever level it is reading. */
interface ReportData {
  primaryMetrics: MetricV2Response[];
  primaryCount: number;
  secondaryMetrics: MetricV2Response[];
  secondaryCount: number;
  metricsLoading: boolean;
  toolTip: string | undefined;
  totalCount: number;
  columns: ReturnType<typeof filterTableData>['visibleColumns'];
  rows: ReturnType<typeof filterTableData>['visibleRows'];
  tableLoading: boolean;
  tableError: Error | null;
  /** False where the level has nothing below it to tabulate. */
  showTable: boolean;
  onExportToCSV: ReturnType<typeof useExportToCSVHandler>;
}

interface ReportArgs extends ReturnType<typeof useTableControls> {
  context: AdminReportOutletContext;
  enabled: boolean;
}

/**
 * The organization levels: roll-ups from `v2/metrics/admin` over every
 * organization in scope, or over the educators of the one being read.
 */
function useOrganizationReport({
  context,
  enabled,
  tableProps,
  searchTerms,
  userSorted,
}: ReportArgs): ReportData {
  const { productId, dateRange, orgIds, teacherIds, isOrgDrilldown } = context;

  const level = currentDrilldown(context);
  const primaryMetricTypes = resolveMetrics(
    ADMIN_PRIMARY_METRICS,
    productId,
    level
  );
  const secondaryMetricTypes = resolveMetrics(
    ADMIN_SECONDARY_METRICS,
    productId,
    level
  );

  const { data: metrics, isLoading: metricsLoading } = useAdminMetricsApi({
    orgIds,
    teacherIds,
    metricTypes: [...primaryMetricTypes, ...secondaryMetricTypes],
    edApps: getEdApps(productId),
    startDate: dateRange.from,
    endDate: dateRange.to,
    enabled,
  });

  // The drill-down reads one organization's educators; the overview reads every
  // organization in scope. Different metric type, different scope field.
  const tableScope = isOrgDrilldown
    ? {
        metricType: AdminMetricTableType.IndividualOrganizationOverview,
        orgId: orgIds[0],
        teacherIds,
      }
    : {
        metricType: AdminMetricTableType.AllOrganizationOverview,
        organizationIds: orgIds,
      };

  const orgLabel = useOrgLabel();
  // Stable, or React Query runs it again on every render.
  const labelOrgs = useCallback(
    (table: MetricsTableV2Response) => labelOrgRows(table, orgLabel),
    [orgLabel]
  );

  const sharedTableArgs = {
    ...tableScope,
    edApps: getEdApps(productId),
    startDate: dateRange.from,
    endDate: dateRange.to,
    pageSize: tableProps.pageSize,
    ...(searchTerms ? { searchTerms } : {}),
    ...buildOrderBy(
      tableProps.sortColumn,
      tableProps.sortDirection,
      searchTerms,
      userSorted
    ),
    select: labelOrgs,
  };

  const {
    data: tableData,
    loading: tableLoading,
    error: tableError,
  } = useAdminMetricsTableApi({
    ...sharedTableArgs,
    page: tableProps.page,
    enabled,
  });

  const { refetch: refetchExportData } = useAdminMetricsTableApi({
    ...sharedTableArgs,
    page: 1,
    fetchAllRecords: true,
    enabled: false,
  });

  const onExportToCSV = useExportToCSVHandler(
    refetchExportData,
    undefined,
    productId,
    'overview'
  );

  // No column rules: the service decides the columns for every product here.
  const { visibleColumns, visibleRows } = filterTableData(tableData);
  const pick = (types: readonly string[]) =>
    (metrics ?? []).filter((metric) => types.includes(metric.metricType));

  return {
    primaryMetrics: pick(primaryMetricTypes),
    primaryCount: primaryMetricTypes.length,
    secondaryMetrics: pick(secondaryMetricTypes),
    secondaryCount: secondaryMetricTypes.length,
    metricsLoading,
    toolTip: tableData?.toolTips ?? undefined,
    totalCount: tableData?.totalRecords ?? 0,
    columns: visibleColumns,
    showTable: true,
    rows: visibleRows,
    tableLoading,
    tableError,
    onExportToCSV,
  };
}

/**
 * The educator drill-down: the teacher Overview's own roster figures and class
 * table, read through `v2/metrics` — the admin roll-up route reports per
 * organization and has no equivalent at this level — only asked for on another
 * educator's roster rather than the reader's own.
 */
function useEducatorReport({
  context,
  enabled,
  tableProps,
  searchTerms,
  userSorted,
}: ReportArgs): ReportData {
  const { productId, dateRange, orgIds, teacherIds, classIds, studentIds } =
    context;
  const orgId = orgIds[0] ?? '';
  const level = currentDrilldown(context);

  const { data: applications } = useApplicationsHierarchy();
  const productCodes = hierarchyProductCodes(applications, productId);

  const { primaryMetricTypes, secondaryMetricTypes } = rosterMetricTypes(
    productId,
    level,
    productCodes
  );

  // The educator's whole roster when no class is picked, the way an empty
  // organization filter means every organization.
  const effectiveClassIds = useEffectiveClassIds({
    orgId,
    userId: teacherIds[0] ?? '',
    edApp: productId,
    selectedClassIds: classIds,
    productCodes,
  });

  const { studentIds: effectiveStudentIds, isReady: studentsReady } =
    useEffectiveStudentIds({
      drilldown: level,
      classIds: effectiveClassIds,
      edApp: productId,
      selectedStudentIds: studentIds,
      productCodes,
    });

  const scope = {
    orgId,
    teacherIds,
    startDate: dateRange.from,
    endDate: dateRange.to,
    edApps: getEdApps(productId),
    classIds: effectiveClassIds,
    studentIds: effectiveStudentIds,
  };
  const canRead = canReadRoster({
    enabled,
    orgId,
    teacherIds,
    classIds: effectiveClassIds,
    studentsReady,
  });
  // The student level reports on one student: there is nothing to tabulate.
  const tableType = ROSTER_TABLE_BY_LEVEL[level];

  const { data: metrics, isLoading: metricsLoading } = useMetricsBatchV2Api({
    ...scope,
    metricTypes: [...primaryMetricTypes, ...secondaryMetricTypes],
    enabled: canRead,
  });

  const sharedTableArgs = {
    ...scope,
    metricType: tableType ?? MetricTableType.AllClassOverview,
    pageSize: tableProps.pageSize,
    ...(searchTerms ? { searchTerms } : {}),
    ...buildOrderBy(
      tableProps.sortColumn,
      tableProps.sortDirection,
      searchTerms,
      userSorted
    ),
  };

  const {
    data: tableData,
    loading: tableLoading,
    error: tableError,
  } = useMetricsTableV2Api({
    ...sharedTableArgs,
    page: tableProps.page,
    enabled: canRead && !!tableType,
  });

  const { refetch: refetchExportData } = useMetricsTableV2Api({
    ...sharedTableArgs,
    page: 1,
    fetchAllRecords: true,
    enabled: false,
  });

  const onExportToCSV = useExportToCSVHandler(
    refetchExportData,
    OVERVIEW_TABLE_COLUMNS,
    productId,
    'overview'
  );

  const { visibleColumns, visibleRows } = filterTableData(
    tableData,
    OVERVIEW_TABLE_COLUMNS,
    productId
  );
  const pick = (types: readonly string[]) =>
    (metrics ?? []).filter((metric) => types.includes(metric.metricType));

  return {
    primaryMetrics: pick(primaryMetricTypes),
    primaryCount: primaryMetricTypes.length,
    secondaryMetrics: pick(secondaryMetricTypes),
    secondaryCount: secondaryMetricTypes.length,
    metricsLoading,
    toolTip: tableData?.toolTips ?? undefined,
    totalCount: tableData?.totalRecords ?? 0,
    columns: visibleColumns,
    showTable: !!tableType,
    rows: visibleRows,
    tableLoading,
    tableError,
    onExportToCSV,
  };
}

/**
 * The admin Overview, at all three levels: every organization, one
 * organization's educators, and one educator's classes. Each level narrows the
 * one above it, so they share this page and differ only in what they read.
 */
export function AdminOverviewPage() {
  const context = useAdminReportOutletContext();
  const {
    productId,
    dateRange,
    selectedOrgCount,
    selectedOrgLabel,
    accessOrgLabel,
    selectedTeacherLabel,
    selectedClassLabel,
    orgIds,
    isScopeLoading,
    teacherIds,
    classIds,
    classCount,
    isOrgDrilldown,
    isTeacherDrilldown,
    isClassDrilldown,
    isStudentDrilldown,
    selectedStudentLabel,
    backTarget,
  } = context;

  const { data: applications } = useApplicationsHierarchy();
  const productLabel =
    applications?.find((app) => app.applicationCode === productId)
      ?.applicationLabel ?? productId;

  // Resolved, and empty: every organization in the filter is outside this product.
  const hasNoScope = !isScopeLoading && orgIds.length === 0;

  const level = currentDrilldown(context);

  const controls = useTableControls({
    defaultSortColumn: SORT_COLUMN_BY_LEVEL[level] ?? ORGANIZATION_COLUMN,
  });
  const { tableProps, reset } = controls;

  // The level is part of the key, not just the filters: drilling in can leave
  // every filter untouched — one educator selected, then opened — and the
  // table controls must still start over, since the level below sorts by a
  // column the level above does not have.
  const filterKey = [
    level,
    productId,
    orgIds.join(','),
    teacherIds.join(','),
    classIds.join(','),
    formatDate(dateRange.from),
    formatDate(dateRange.to),
  ].join('|');
  const [previousFilterKey, setPreviousFilterKey] = useState(filterKey);
  if (filterKey !== previousFilterKey) {
    setPreviousFilterKey(filterKey);
    reset();
  }

  // The educator and class levels both read the roster routes.
  const readsRoster =
    isTeacherDrilldown || isClassDrilldown || isStudentDrilldown;

  // Both are called, as hooks must be; only the level being read fetches.
  const organizationReport = useOrganizationReport({
    ...controls,
    context,
    enabled: !readsRoster,
  });
  const educatorReport = useEducatorReport({
    ...controls,
    context,
    enabled: readsRoster,
  });
  const report = readsRoster ? educatorReport : organizationReport;

  const headingTitle = headingTitleFor(context, {
    selectedOrgLabel,
    selectedTeacherLabel,
    selectedClassLabel,
    selectedStudentLabel,
  });
  const headingCount = headingCountFor(context, report.totalCount, classCount);

  // Only a non-default range is on the URL, so the default carries nothing.
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Back to the report root with every scope filter dropped — at a drill-down
  // the level itself is part of the scope, so the path goes too.
  const viewAllOrganizations = useCallback(() => {
    const search = new URLSearchParams();
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (from && to) {
      search.set('from', from);
      search.set('to', to);
    }
    navigate({
      pathname: `/data/${productId}/overview`,
      search: search.toString(),
    });
  }, [navigate, productId, searchParams]);

  // A row is an organization on the overview and an educator on the
  // organization drill-down, so each steps one level further in. The educator's
  // classes have no level below them.
  const handleDrilldown = useCallback(
    (rowId: string | number) => {
      const search = new URLSearchParams();
      const from = searchParams.get('from');
      const to = searchParams.get('to');
      if (from && to) {
        search.set('from', from);
        search.set('to', to);
      }

      if (isClassDrilldown) {
        search.set('orgId', String(orgIds[0]));
        search.set('teacherId', String(teacherIds[0]));
        search.set('classId', String(classIds[0]));
        search.set('studentId', String(rowId));
        navigate(
          `/data/${productId}/overview/${
            Drilldown.Student
          }?${search.toString()}`
        );
        return;
      }

      if (isTeacherDrilldown) {
        search.set('orgId', String(orgIds[0]));
        search.set('teacherId', String(teacherIds[0]));
        search.set('classId', String(rowId));
        navigate(
          `/data/${productId}/overview/${Drilldown.Class}?${search.toString()}`
        );
        return;
      }

      if (isOrgDrilldown) {
        search.set('orgId', String(orgIds[0]));
        search.set('teacherId', String(rowId));
        navigate(
          `/data/${productId}/overview/${
            Drilldown.Teacher
          }?${search.toString()}`
        );
        return;
      }

      search.set('orgId', String(rowId));
      navigate(
        `/data/${productId}/overview/${
          Drilldown.Organization
        }?${search.toString()}`
      );
    },
    [
      navigate,
      productId,
      searchParams,
      isOrgDrilldown,
      isTeacherDrilldown,
      isClassDrilldown,
      orgIds,
      teacherIds,
      classIds,
    ]
  );

  return (
    <div className="flex min-w-0 flex-col">
      <ProductHeader productId={productId} drilldown={level} />

      {hasNoScope && (
        <HighlightsCard title={headingTitle} backTarget={backTarget}>
          <NoProductAccess
            orgLabel={accessOrgLabel}
            productLabel={productLabel}
            isSingleOrg={selectedOrgCount === 1}
            {...(selectedOrgCount > 0
              ? { onViewAllOrganizations: viewAllOrganizations }
              : {})}
          />
        </HighlightsCard>
      )}

      {!hasNoScope && (
        <>
          <HighlightsCard
            title={headingTitle}
            backTarget={backTarget}
            {...headingCount}
          >
            <div className="mt-4">
              <Metrics
                metrics={report.primaryMetrics}
                isLoading={report.metricsLoading}
                count={report.primaryCount}
              />
            </div>

            {report.secondaryCount > 0 && (
              <Metrics
                metrics={report.secondaryMetrics}
                isLoading={report.metricsLoading}
                count={report.secondaryCount}
                variant="secondary"
              />
            )}
          </HighlightsCard>

          {report.showTable && (
            <>
              <div className="flex items-center gap-2 pb-4 pt-8">
                {/* Museo Sans 600, 20px, 150% line height — the design sets
                    Details below the product title, not level with it. */}
                <span className="text-xl font-semibold leading-[1.5] text-neutral-1300">
                  Details
                </span>
                {report.toolTip && (
                  <Popover
                    trigger={
                      <button
                        aria-label={report.toolTip}
                        className="flex size-4 items-center justify-center"
                        type="button"
                      >
                        <InfoIcon aria-hidden="true" />
                      </button>
                    }
                  >
                    {report.toolTip}
                  </Popover>
                )}
              </div>
              <Table
                {...tableProps}
                loading={report.tableLoading}
                columns={report.columns}
                rows={report.rows}
                totalCount={report.totalCount}
                onExportToCSV={report.onExportToCSV}
                onDrilldown={handleDrilldown}
                error={report.tableError}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
